import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Clock, Zap, Target, CheckCircle2, ArrowRight, X } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { ChartSkeleton, KpiCardSkeleton } from '@/components/Skeleton';
import { api } from '@/lib/api';
import type { Demanda } from '@/lib/types';

const tooltipStyle = {
  backgroundColor: '#0a1020',
  border: '1px solid #3b82f6',
  borderRadius: 8,
  fontSize: 12,
  color: '#e8eefc',
  padding: '6px 10px',
  boxShadow: '0 4px 12px rgba(0,0,0,.5)',
};
const tooltipLabelStyle = { color: '#60a5fa', fontWeight: 600, fontSize: 11 };
const tooltipItemStyle = { color: '#e8eefc' };

type Drill = 'ativas' | 'atrasadas' | 'aprovacao' | null;

export function DashProducao() {
  const [drill, setDrill] = useState<Drill>(null);

  const demandasQ = useQuery({ queryKey: ['demandas'], queryFn: () => api.demandas.list().then((r) => r.data) });
  const areasQ = useQuery({ queryKey: ['areas'], queryFn: () => api.areas.list().then((r) => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then((r) => r.data) });

  const demandas = demandasQ.data ?? [];
  const areas = areasQ.data ?? [];
  const solicitantes = solicitantesQ.data ?? [];
  const areaById = useMemo(() => new Map(areas.map((a) => [a.id, a])), [areas]);
  const solById = useMemo(() => new Map(solicitantes.map((s) => [s.id, s.nome])), [solicitantes]);

  const ativas = demandas.filter((d) =>
    ['Triagem', 'Em produção', 'Em revisão', 'Aguardando aprovação'].includes(d.status));
  const atrasadas = ativas.filter((d) => d.prazoSla && new Date(d.prazoSla) < new Date());
  const aprovacao = demandas.filter((d) => d.status === 'Aguardando aprovação');
  const noPrazo = ativas.length - atrasadas.length;
  const pctPrazo = ativas.length ? Math.round((noPrazo / ativas.length) * 100) : 100;

  const porArea = useMemo(() => {
    const m: Record<string, number> = {};
    for (const d of ativas) {
      for (const id of d.areasIds) {
        const nome = areaById.get(id)?.nome ?? '—';
        m[nome] = (m[nome] ?? 0) + 1;
      }
    }
    return Object.entries(m).sort((a, b) => b[1] - a[1]).map(([nome, n]) => ({ nome, n }));
  }, [ativas, areaById]);

  const porStatus = [
    { status: 'Briefing',  n: ativas.filter((d) => d.status === 'Triagem').length, color: '#60a5fa' },
    { status: 'Produção',  n: ativas.filter((d) => d.status === 'Em produção').length, color: '#f59e0b' },
    { status: 'Revisão',   n: ativas.filter((d) => d.status === 'Em revisão').length, color: '#8b5cf6' },
    { status: 'Aprovação', n: aprovacao.length, color: '#ec4899' },
  ];

  const loading = demandasQ.isLoading || areasQ.isLoading;

  const drillSet: Record<Exclude<Drill, null>, { titulo: string; lista: Demanda[] }> = {
    ativas:     { titulo: 'Demandas ativas',         lista: ativas },
    atrasadas:  { titulo: 'Demandas atrasadas',      lista: atrasadas },
    aprovacao:  { titulo: 'Aguardando aprovação',    lista: aprovacao },
  };

  return (
    <div>
      <PageHeader title="Dash Demandas" />
      <div className="px-6 py-4 space-y-4">
        {loading ? (
          <>
            <div className="grid grid-cols-4 gap-3">{[1,2,3,4].map((i) => <KpiCardSkeleton key={i} />)}</div>
            <div className="grid grid-cols-2 gap-3"><ChartSkeleton /><ChartSkeleton /></div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-3">
              <button type="button" onClick={() => setDrill('ativas')} className="text-left">
                <KpiCard label="Demandas ativas" color="blue" value={ativas.length}
                  delta="no pipeline produtivo · ver lista →" icon={<Zap className="w-4 h-4" />} />
              </button>
              <KpiCard label="No prazo" color="green" value={`${pctPrazo}%`}
                delta={`${noPrazo} de ${ativas.length}`} icon={<Target className="w-4 h-4" />} />
              <button type="button" onClick={() => setDrill('atrasadas')} className="text-left">
                <KpiCard label="Atrasadas" color="orange" value={atrasadas.length}
                  delta="SLA vencido · ver lista →" icon={<Clock className="w-4 h-4" />} />
              </button>
              <button type="button" onClick={() => setDrill('aprovacao')} className="text-left">
                <KpiCard label="Aguard. aprovação" color="purple" value={aprovacao.length}
                  delta="aguardando OK · ver lista →" icon={<CheckCircle2 className="w-4 h-4" />} />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="card">
                <h3 className="text-sm font-bold mb-1">Pipeline ativo por status</h3>
                <p className="text-xs text-muted mb-3">Distribuição entre fases produtivas</p>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={porStatus} margin={{ top: 8, right: 4, left: -20, bottom: 4 }}>
                      <CartesianGrid stroke="#1f2d4d" vertical={false} strokeDasharray="3 3" />
                      <XAxis dataKey="status" tick={{ fill: '#7d8aa5', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#7d8aa5', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ fill: 'rgba(59,130,246,0.05)' }} />
                      <Bar dataKey="n" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                        {porStatus.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="card">
                <h3 className="text-sm font-bold mb-1">Carga por área executora</h3>
                <p className="text-xs text-muted mb-3">Demandas ativas alocadas em cada time</p>
                <div className="space-y-1.5">
                  {porArea.length === 0 && <div className="text-xs text-muted py-4 text-center">sem áreas alocadas</div>}
                  {porArea.map(({ nome, n }) => {
                    const max = Math.max(...porArea.map((a) => a.n));
                    return (
                      <div key={nome} className="flex items-center gap-2 text-xs">
                        <div className="w-32 text-text-2 truncate">{nome}</div>
                        <div className="flex-1 h-4 bg-panel-2 rounded">
                          <div className="h-full bg-gradient-to-r from-blue to-blue-light rounded" style={{ width: `${(n / max) * 100}%` }} />
                        </div>
                        <div className="w-6 text-right font-bold">{n}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {drill && (
        <DrillModal
          titulo={drillSet[drill].titulo}
          demandas={drillSet[drill].lista}
          solById={solById}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

function DrillModal({
  titulo, demandas, solById, onClose,
}: { titulo: string; demandas: Demanda[]; solById: Map<string, string>; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-panel border border-border rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-panel z-10">
          <h2 className="text-sm font-bold">{titulo} <span className="text-muted font-normal">· {demandas.length}</span></h2>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {demandas.length === 0 && (
            <div className="col-span-full text-center py-8 text-sm text-muted">Nenhuma demanda nessa categoria.</div>
          )}
          {demandas.map((d) => (
            <Link
              key={d.id}
              to={`/coordenacao?busca=1&abrir=${d.id}`}
              className="bg-panel-2 border border-border rounded p-2.5 text-xs hover:border-blue/50 transition-colors group"
            >
              <div className="font-semibold leading-snug line-clamp-2 mb-1">{d.titulo}</div>
              <div className="text-[10px] text-text-2 truncate mb-1">{solById.get(d.solicitanteId) ?? '—'}</div>
              <div className="flex items-center justify-between text-[10px] text-muted">
                <span>{d.prazoSla ? `prazo ${new Date(d.prazoSla).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}` : 'sem prazo'}</span>
                <ArrowRight className="w-3 h-3 group-hover:text-blue-light transition-colors" />
              </div>
            </Link>
          ))}
        </div>
        <div className="px-5 py-2 border-t border-border bg-panel-2 text-[10px] text-muted">
          Clique num card pra abrir no Kanban da Coordenação.
        </div>
      </div>
    </div>
  );
}
